import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { startGoogleLogin } from "@/lib/googleAuth";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Gift, Home, Wrench, HardHat } from "lucide-react";
import { toast } from "sonner";

export default function Signup() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await register(name, email, password);
      toast.success("Account created — your complimentary analysis is ready");
      navigate("/dashboard");
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || "Unable to create account.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#050914]">
      <div className="relative hidden lg:flex flex-col p-10 xl:p-12 gx-grid-bg border-r border-slate-800 overflow-hidden">
        <div className="absolute inset-0 gx-radial" />
        <div className="relative z-10">
          <Logo size="lg" />
        </div>
        <div className="relative z-10 flex-1 flex flex-col justify-center max-w-lg">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full border border-cyan-400/30 bg-cyan-400/10 px-3 py-1 text-xs font-medium text-cyan-300">
              <Gift className="h-3.5 w-3.5" /> 1 complimentary analysis
            </span>
          </div>
          <h2 className="mt-6 font-display text-4xl xl:text-5xl font-bold tracking-tight text-white leading-[1.1]">
            Understand every <span className="text-cyan-400 gx-text-glow">conversation</span>.
          </h2>
          <p className="mt-5 text-lg text-slate-400 leading-relaxed">
            New users receive one complimentary full analysis — use it within 24 hours of signing
            up.
          </p>
        </div>
        <div className="relative z-10 text-sm text-slate-500">
          Glexis is a decision-support tool, not a lie detector.
        </div>
      </div>

      <div className="flex items-start lg:items-center justify-center overflow-y-auto p-6 sm:p-10">
        <div className="w-full max-w-md py-6">
          <div className="lg:hidden mb-8">
            <Logo size="lg" />
          </div>

          <section data-testid="intro-purpose" className="mb-6 rounded-xl border border-slate-800 bg-slate-900/40 p-5 sm:p-6">
            <div className="flex items-start justify-between gap-3">
              <h2 className="font-display text-2xl font-bold text-white">Are You Getting the Full Picture?</h2>
            </div>
            <p className="mt-3 text-[15px] leading-relaxed text-slate-300">
              Have you ever wondered whether someone is sharing all the information they have about a situation?
            </p>
            <ul className="mt-4 space-y-2.5">
              {[
                { icon: Home, text: "Is a realtor telling you everything they know about the home you're considering?" },
                { icon: Wrench, text: "Is a mechanic clearly explaining what your vehicle needs and why?" },
                { icon: HardHat, text: "Is a contractor giving you the complete picture about a repair, timeline, or cost?" },
              ].map(({ icon: Icon, text }, i) => (
                <li key={i} className="flex items-start gap-2.5 text-[15px] leading-snug text-slate-300">
                  <Icon aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-cyan-400" />
                  <span>{text}</span>
                </li>
              ))}
            </ul>
            <p className="mt-4 text-sm leading-relaxed text-slate-500">
              Glexis does not label someone as truthful or dishonest—it helps you notice communication
              patterns, identify areas that may need a follow-up question, and make more confident decisions.
            </p>
            <div className="mt-4 flex items-center justify-between border-t border-slate-800 pt-3">
              <span className="text-sm text-slate-400">Already have an account?</span>
              <Link to="/login" data-testid="intro-signin-link" className="rounded text-sm font-semibold text-cyan-400 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400 focus-visible:ring-offset-2 focus-visible:ring-offset-[#050914]">
                Sign in
              </Link>
            </div>
          </section>

          <h1 className="font-display text-3xl font-bold tracking-tight text-white">Create account</h1>
          <p className="mt-2 text-slate-400">Start with a complimentary analysis.</p>

          <Button
            type="button"
            onClick={startGoogleLogin}
            data-testid="google-signup-btn"
            variant="outline"
            className="mt-6 w-full h-11 border-slate-700 bg-slate-900/40 text-white hover:bg-slate-800 hover:text-white"
          >
            <GoogleIcon /> Continue with Google
          </Button>

          <div className="my-5 flex items-center gap-4 text-xs text-slate-600">
            <span className="h-px flex-1 bg-slate-800" /> OR <span className="h-px flex-1 bg-slate-800" />
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="name" className="text-slate-300">Name</Label>
              <Input
                id="name"
                required
                data-testid="signup-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="mt-1.5 h-11 bg-slate-900/50 border-slate-700 text-white focus-visible:ring-cyan-400"
                placeholder="Jane Doe"
              />
            </div>
            <div>
              <Label htmlFor="email" className="text-slate-300">Email</Label>
              <Input
                id="email"
                type="email"
                required
                data-testid="signup-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1.5 h-11 bg-slate-900/50 border-slate-700 text-white focus-visible:ring-cyan-400"
                placeholder="you@example.com"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-slate-300">Password</Label>
              <Input
                id="password"
                type="password"
                required
                minLength={6}
                data-testid="signup-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5 h-11 bg-slate-900/50 border-slate-700 text-white focus-visible:ring-cyan-400"
                placeholder="At least 6 characters"
              />
            </div>
            {error && (
              <p className="text-sm text-red-400" data-testid="signup-error">{error}</p>
            )}
            <Button
              type="submit"
              disabled={loading}
              data-testid="signup-submit"
              className="w-full h-11 bg-cyan-400 text-black font-semibold hover:bg-cyan-300 gx-glow"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create account"}
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-slate-400">
            Already have an account?{" "}
            <Link to="/login" data-testid="go-login" className="rounded text-cyan-400 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400 focus-visible:ring-offset-2 focus-visible:ring-offset-[#050914]">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/>
      <path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z"/>
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/>
    </svg>
  );
}
